import { inject, Injectable, InjectionToken } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { gt, parse } from "semver";
import { EInvoiceFormatMatch, EInvoiceImportResult, EInvoiceValidationMessage, EInvoiceValidationResult, EINVOICE_PROCESSING_ENGINE, EinvoiceProcessingEngine } from "../engine/einvoice-processing-engine";
import { FormatMatchResult } from "../interfaces/format-match-result.interface";
import type { Attachment } from "./einvoice-viewer.service";

declare const $localize: (messageParts: TemplateStringsArray, ...expressions: any[]) => string;

// SchemaField interface defined locally
interface SchemaField {
  propertyName: string;
  fullPath: string;
  title: string;
  description: string;
  btcode?: string | null;
}

export interface FacturXContent {
  xml: string;
  xmlFileName: string;
  additionalAttachments: Attachment[];
  pdfBytes: Uint8Array;
}

const PDF_ATTACHMENT_EXTRACTION_TIMEOUT_MS = 30_000;

export interface InvoiceServiceConfig {
  schemaPath?: string;
  pdfWorkerPath?: string;
}

export const INVOICE_SERVICE_CONFIG = new InjectionToken<InvoiceServiceConfig>('InvoiceServiceConfig');

const DEFAULT_MODEL_ID = 'urn:cen.eu:en16931:2017';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private readonly processor = inject(EINVOICE_PROCESSING_ENGINE);
  private _initialized: boolean = false;
  private initializePromise: Promise<void> | null = null;
  private pdfWorkerPath: string;
  private readonly modelSchemaCache = new Map<string, unknown>();

  public initialized$ = new BehaviorSubject<boolean>(false);

  constructor() {
    const config = inject(INVOICE_SERVICE_CONFIG, { optional: true });
    this.pdfWorkerPath = config?.pdfWorkerPath ?? "./assets/pdfjs/pdf.worker.min.mjs";
  }

  public get initialized(): boolean {
    return this._initialized;
  }

  public async detectFormat(xml: string): Promise<{ matchedFormat?: EInvoiceFormatMatch; errors: EInvoiceValidationMessage[] }> {
    await this.ensureProcessorInitialized();

    const matchedFormats = await this.processor.probe(xml, {});
    const releasedFormats = matchedFormats.filter(f => f.status === "released");
    if (releasedFormats.length === 0) {
      const deprecatedFormats = matchedFormats.filter(f => f.status === "deprecated");
      return {
        errors: [{
          message: deprecatedFormats?.length === 0
            ? $localize`:@@einvoice.processing.error.unsupported-version:Unsupported e-invoice version.`
            : $localize`:@@einvoice.processing.error.unsupported-version-format:Unsupported e-invoice version: ` + " " + deprecatedFormats[0].name + ` v` + deprecatedFormats[0].version,
        }],
      };
    }

    let version = parse(releasedFormats[0].version);
    let format = releasedFormats[0];
    for (let i = 1; i < releasedFormats.length; i++) {
      const v = parse(releasedFormats[i].version);
      if (v != null && (version == null || gt(v, version))) {
        version = v;
        format = releasedFormats[i];
      }
    }

    return { matchedFormat: format, errors: [] };
  }

  public async validate(xml: string, matchedFormat?: EInvoiceFormatMatch): Promise<EInvoiceValidationResult> {
    await this.ensureProcessorInitialized();

    let format = matchedFormat;
    if (format == null) {
      const detection = await this.detectFormat(xml);
      if (detection.matchedFormat == null) {
        return {
          valid: false,
          errors: detection.errors,
          stepResults: [],
        };
      }
      format = detection.matchedFormat;
    }

    const validationResult = await this.processor.validate(xml, {
      formatId: format.id,
      syntax: format.syntax,
      version: format.version
    });

    return {
      ...validationResult,
      matchedFormat: validationResult.matchedFormat ?? format
    };
  }

  public async importModel(xml: string, format: string, version: string, syntax: string, trackSource = true): Promise<EInvoiceImportResult> {
    await this.ensureProcessorInitialized();

    return await this.processor.importToModel(xml, format, version, syntax, DEFAULT_MODEL_ID, trackSource);
  }

  public async getModelSchema(format: FormatMatchResult, modelId = DEFAULT_MODEL_ID): Promise<unknown | undefined> {
    await this.ensureProcessorInitialized();

    const cacheKey = `${format.id}|${format.version}|${format.syntax}|${modelId}`;
    const cachedSchema = this.modelSchemaCache.get(cacheKey);
    if (cachedSchema !== undefined) {
      return cachedSchema;
    }

    const schemaText = await this.processor.getModelSchema(format.id, format.version, format.syntax, modelId);
    if (!schemaText) {
      return undefined;
    }

    const schema = JSON.parse(schemaText) as unknown;
    this.modelSchemaCache.set(cacheKey, schema);
    return schema;
  }

  private async ensureProcessorInitialized(): Promise<void> {
    if (this._initialized) {
      return;
    }

    if (this.initializePromise == null) {
      this.initializePromise = Promise.resolve(this.processor.init?.())
        .then(() => {
          this._initialized = true;
          this.initialized$.next(true);
        })
        .catch((error) => {
          this.initializePromise = null;
          throw error;
        });
    }

    await this.initializePromise;
  }

  // Function to traverse the JSON schema and extract required fields
  extractFields(schema: any, path: string = ''): SchemaField[] {
    let fields: SchemaField[] = [];
    const currentItems = schema.definitions ?? schema.properties;

    if (currentItems) {
      for (const item in currentItems) {
        const value = currentItems[item];

        const fullPath = path ? `${path}.${item}` : item;
        const title = value.title || '';
        const description = value.description || '';
        const btcode = value.title?.match(/B(T|G)-\d+/g) ?? [];
        console.log(path, fullPath);
        fields.push({ propertyName: item, fullPath, title, description, btcode: btcode.length > 0 ? btcode[0] : null });
        fields = fields.concat(this.extractFields(value, fullPath));
      }
    }
    return fields;
  }

  public async getJsonSchemaFields(format: FormatMatchResult, modelId = DEFAULT_MODEL_ID): Promise<SchemaField[]> {
    const schema = await this.getModelSchema(format, modelId);
    if (!schema) {
      return [];
    }

    const fields = this.extractFields(schema);
    return fields;
  }

  public async extractPdfAttachments(pdf: ArrayBuffer): Promise<Attachment[]> {
    const startedAt = performance.now();
    console.info('[pdf-xml-probe] extractPdfAttachments:start', {
      bytes: pdf.byteLength,
      timeoutMs: PDF_ATTACHMENT_EXTRACTION_TIMEOUT_MS,
    });

    const module = await import(/* webpackChunkName: "pdfjs" */ "pdfjs-dist");
    module.GlobalWorkerOptions.workerSrc = this.pdfWorkerPath;
    const loadingTask = module.getDocument({ data: new Uint8Array(pdf.slice(0)) });

    try {
      const proxy = await this.withTimeout(
        loadingTask.promise,
        PDF_ATTACHMENT_EXTRACTION_TIMEOUT_MS,
        'pdf document load',
      );
      console.info('[pdf-xml-probe] extractPdfAttachments:document-loaded', {
        elapsedMs: Math.round(performance.now() - startedAt),
      });

      const attachments = await this.withTimeout(
        proxy.getAttachments(),
        PDF_ATTACHMENT_EXTRACTION_TIMEOUT_MS,
        'pdf attachment read',
      );
      console.info('[pdf-xml-probe] extractPdfAttachments:attachments-read', {
        elapsedMs: Math.round(performance.now() - startedAt),
        attachmentCount: attachments?.size ?? 0,
      });

      if (!attachments || attachments.size === 0) {
        return [];
      }

      const result: Attachment[] = [];
      for (const [id, attachment] of attachments) {
        const content = attachment.content ?? await proxy.getAttachmentContent(id);
        if (content) {
          result.push({ content, filename: attachment.filename });
        }
      }
      return result;
    } finally {
      try {
        await loadingTask.destroy?.();
      } catch (destroyError) {
        console.warn('[pdf-xml-probe] extractPdfAttachments:destroy-failed', {
          elapsedMs: Math.round(performance.now() - startedAt),
          error: destroyError instanceof Error ? destroyError.message : String(destroyError),
        });
      }
    }
  }

  public async extractXml(pdf: ArrayBuffer): Promise<FacturXContent | null> {
    const pdfBytes = new Uint8Array(pdf.slice(0));
    const attachments = await this.extractPdfAttachments(pdf);
    const xml = attachments.find(att => att.filename === "factur-x.xml" || att.filename === "xrechnung.xml");
    if (xml !== undefined) {
      const decoder = new TextDecoder();
      return {
        xml: decoder.decode(xml.content),
        xmlFileName: xml.filename,
        additionalAttachments: attachments.filter(att => att.filename !== xml.filename),
        pdfBytes
      };
    }
    return null;
  }

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(new Error(`${label} timed out after ${timeoutMs}ms`));
      }, timeoutMs);

      promise.then(
        (value) => {
          clearTimeout(timeoutId);
          resolve(value);
        },
        (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      );
    });
  }
}
